import { useCallback, useEffect, useMemo } from 'react';

/**
 * Cascade selection shape for hierarchy filtering.
 *
 * @typedef {Object} HierarchySelection
 * @property {string} organization
 * @property {string} region
 * @property {string} facility
 * @property {string} building
 */

/**
 * Nested hierarchy map organized as Organization -> Region -> Facility -> Building.
 *
 * @typedef {Record<string, Record<string, Record<string, Record<string, unknown>>>>} HierarchyMap
 */

/** @type {HierarchySelection} */
export const defaultHierarchySelection = {
	organization: '',
	region: '',
	facility: '',
	building: '',
};

// Helper to create a deep clone of a hierarchy selection object.
function cloneSelection(selection = {}) {
	return {
		organization: selection.organization || '',
		region: selection.region || '',
		facility: selection.facility || '',
		building: selection.building || '',
	};
}

// Helper to compare two hierarchy selections for equality.
function areSelectionsEqual(selectionA, selectionB) {
	return selectionA.organization === selectionB.organization
		&& selectionA.region === selectionB.region
		&& selectionA.facility === selectionB.facility
		&& selectionA.building === selectionB.building;
}

// Helper to normalize a hierarchy selection against the available hierarchy options, ensuring cascade consistency.
function normalizeHierarchySelection(hierarchy, selection) {
	const safeHierarchy = hierarchy || {};
	const normalizedSelection = cloneSelection(selection);

	// If the selected organization is not valid, reset the entire selection.
	if (!normalizedSelection.organization || !safeHierarchy[normalizedSelection.organization]) {
		return cloneSelection(defaultHierarchySelection);
	}

	// If the selected region is not valid under the selected organization, reset region, facility, and building.
	const regionMap = safeHierarchy[normalizedSelection.organization] || {};
	if (!normalizedSelection.region || !regionMap[normalizedSelection.region]) {
		normalizedSelection.region = '';
		normalizedSelection.facility = '';
		normalizedSelection.building = '';
		return normalizedSelection;
	}

	// If the selected facility is not valid under the selected organization and region, reset facility and building.
	const facilityMap = regionMap[normalizedSelection.region] || {};
	if (!normalizedSelection.facility || !facilityMap[normalizedSelection.facility]) {
		normalizedSelection.facility = '';
		normalizedSelection.building = '';
		return normalizedSelection;
	}

	// If the selected building is not valid under the selected organization, region, and facility, reset building.
	const buildingMap = facilityMap[normalizedSelection.facility] || {};
	if (!normalizedSelection.building || !buildingMap[normalizedSelection.building]) {
		normalizedSelection.building = '';
	}

	return normalizedSelection;
}

/**
 * Provides derived hierarchy options and cascade-safe setter functions.
 *
 * @param {{
 * 	hierarchy?: HierarchyMap,
 * 	selection: HierarchySelection,
 * 	onSelectionChange: (nextSelection: HierarchySelection) => void
 * }} params
 * @returns {{
 * 	organizationOptions: string[],
 * 	regionOptions: string[],
 * 	facilityOptions: string[],
 * 	buildingOptions: string[],
 * 	setOrganization: (organization: string) => void,
 * 	setRegion: (region: string) => void,
 * 	setFacility: (facility: string) => void,
 * 	setBuilding: (building: string) => void,
 * 	clearSelection: () => void,
 * 	hasSelection: boolean
 * }}
 */
export default function useHierarchyCascade({ hierarchy = {}, selection, onSelectionChange }) {
	const safeHierarchy = useMemo(() => hierarchy || {}, [hierarchy]);
	const safeSelection = useMemo(() => cloneSelection(selection), [selection]);

	// Derive options for each level of the hierarchy based on the current selection and available hierarchy data.
	const organizationOptions = useMemo(() => Object.keys(safeHierarchy), [safeHierarchy]);

	// Regions depend on the selected organization. If no organization is selected, there are no region options.
	const regionOptions = useMemo(() => {
		if (!safeSelection.organization || !safeHierarchy[safeSelection.organization]) {
			return [];
		}

		return Object.keys(safeHierarchy[safeSelection.organization]);
	}, [safeHierarchy, safeSelection.organization]);

	// Facilities depend on the selected organization and region. If either is not selected, there are no facility options.
	const facilityOptions = useMemo(() => {
		if (!safeSelection.organization || !safeSelection.region) {
			return [];
		}

		if (!safeHierarchy[safeSelection.organization]?.[safeSelection.region]) {
			return [];
		}

		return Object.keys(safeHierarchy[safeSelection.organization][safeSelection.region]);
	}, [safeHierarchy, safeSelection.organization, safeSelection.region]);

	// Buildings depend on the selected organization, region, and facility. If any is not selected, there are no building options.
	const buildingOptions = useMemo(() => {
		if (!safeSelection.organization || !safeSelection.region || !safeSelection.facility) {
			return [];
		}

		if (!safeHierarchy[safeSelection.organization]?.[safeSelection.region]?.[safeSelection.facility]) {
			return [];
		}

		return Object.keys(safeHierarchy[safeSelection.organization][safeSelection.region][safeSelection.facility]);
	}, [safeHierarchy, safeSelection.organization, safeSelection.region, safeSelection.facility]);

	// Effect to normalize the selection whenever the hierarchy or selection changes, ensuring cascade consistency.
	// If the normalized selection differs from the current selection, emit the normalized selection.
	useEffect(() => {
		const normalizedSelection = normalizeHierarchySelection(safeHierarchy, safeSelection);
		if (!areSelectionsEqual(normalizedSelection, safeSelection)) {
			onSelectionChange(normalizedSelection);
		}
	}, [onSelectionChange, safeHierarchy, safeSelection]);

	// When setting organization, reset the entire selection to ensure cascade consistency.
	const setOrganization = useCallback((organization) => {
		onSelectionChange({
			organization: organization || '',
			region: '',
			facility: '',
			building: '',
		});
	}, [onSelectionChange]);

	// When setting region, if the current organization is not valid, reset the entire selection. Otherwise, update region and reset dependent levels.
	const setRegion = useCallback((region) => {
		if (!safeSelection.organization) {
			onSelectionChange(cloneSelection(defaultHierarchySelection));
			return;
		}

		onSelectionChange({
			organization: safeSelection.organization,
			region: region || '',
			facility: '',
			building: '',
		});
	}, [onSelectionChange, safeSelection.organization]);

	// When setting facility, if the current organization or region is not valid, reset the entire selection. Otherwise, update facility and reset dependent level.
	const setFacility = useCallback((facility) => {
		if (!safeSelection.organization || !safeSelection.region) {
			onSelectionChange(cloneSelection(defaultHierarchySelection));
			return;
		}

		onSelectionChange({
			organization: safeSelection.organization,
			region: safeSelection.region,
			facility: facility || '',
			building: '',
		});
	}, [onSelectionChange, safeSelection.organization, safeSelection.region]);

	// When setting building, if the current organization, region, or facility is not valid, reset the entire selection. Otherwise, update building.
	const setBuilding = useCallback((building) => {
		if (!safeSelection.organization || !safeSelection.region || !safeSelection.facility) {
			onSelectionChange(cloneSelection(defaultHierarchySelection));
			return;
		}

		onSelectionChange({
			organization: safeSelection.organization,
			region: safeSelection.region,
			facility: safeSelection.facility,
			building: building || '',
		});
	}, [onSelectionChange, safeSelection.organization, safeSelection.region, safeSelection.facility]);

	// Clear selection resets to default empty values.
	const clearSelection = useCallback(() => {
		onSelectionChange(cloneSelection(defaultHierarchySelection));
	}, [onSelectionChange]);

	// Determine if there is any selection made at any level of the hierarchy. This can be used to conditionally render UI elements related to the hierarchy filtering.
	const hasSelection = (
		safeSelection.organization !== '' ||
		safeSelection.region !== '' ||
		safeSelection.facility !== '' ||
		safeSelection.building !== ''
	);

	return {
		organizationOptions,
		regionOptions,
		facilityOptions,
		buildingOptions,
		setOrganization,
		setRegion,
		setFacility,
		setBuilding,
		clearSelection,
		hasSelection,
	};
}
